import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, IconButton, ListCard, Modal, Text, useComponentTheme } from '@vmos2/design-system';
import omit from 'lodash/omit';
import get from 'lodash/get';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useDeviceSize } from '@vmos2/providers';

import { AddressDetails, DeliveryInstructions, FooterButton, getAddressTitle, LocationPin } from './index';

type Props = {
  Form: Node,
  onUpdate: Function,
  onClose: () => void,
  deliveryAddress?: {
    address: {
      address1: string,
      address2?: string,
      town: string,
      country: string,
      postcode?: string,
      location: {
        lat: number,
        long: number,
      },
      label?: string,
      uuid: string,
      subtitle?: string,
    },
    instructions: {
      phoneNumber?: string,
      deliveryInstructions?: string,
    },
    label?: string,
  },
  handleAvailableForDelivery?: Function;
}
export const EditDeliveryAddressModal = ({ Form, deliveryAddress, onUpdate, onClose, handleAvailableForDelivery }: Props) => {
  const { isMobile } = useDeviceSize();
  const { t } = useTranslation();
  const [newAddress, setNewAddress] = useState(null);
  const [wizardStep, setWizardStep] = useState(0);
  const [hasErrors, setErrors] = useState(false);

  const listCardTheme = useComponentTheme(theme => theme.components.ListCard);

  useEffect(() => {
    if (deliveryAddress) {
      setNewAddress({
        address: {
          ...deliveryAddress.address,
          location: {
            latitude: Number(deliveryAddress.address.location.lat),
            longitude: Number(deliveryAddress.address.location.long),
          }
        },
        ...omit(deliveryAddress, ['address']),
        uuid: deliveryAddress.address.uuid,
      });
    }
  }, [deliveryAddress]);

  const handleClose = useCallback(() => {
    setWizardStep(0);
    setNewAddress(null);
    onClose();
  }, [onClose]);
  const handleMapClick = useCallback(() => setWizardStep(1), []);

  const handleSaveMap = useCallback( async () => {
    onUpdate({
      uuid: newAddress.uuid,
      address: {
        location: {
          lat: newAddress.address.location.latitude,
          long: newAddress.address.location.longitude,
        }
      }
    });
    setWizardStep(0);
  }, [newAddress, onUpdate]);

  const handleSaveDetails = useCallback(async () => {
    await onUpdate({
      uuid: newAddress.uuid,
      address: {
        ...omit(newAddress.address, ['location']),
      }
    });
    setWizardStep(4);
  }, [newAddress, onUpdate]);

  const handleSaveInstructions = useCallback(async () => {
    await onUpdate({
      uuid: newAddress.uuid,
      instructions: newAddress.instructions,
    });
    setWizardStep(0);
  }, [newAddress, onUpdate]);

  const handleChangeAddressDetails = (field, value) => {
    setNewAddress(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value,
      },
    }));
  };

  const handleChangePosition = (field, value) => {
    setNewAddress(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value,
      },
    }));
  };

  const handleChangeInstructions = (field, value) => {
    setNewAddress(prev => ({
      ...prev,
      instructions: {
        ...prev.instructions,
        [field]: value,
      },
    }));
  };

  const handleBackButton = useCallback(() => {
    setWizardStep(prev => (prev === 4 ? 2 : 0));
  }, []);

  const renderFooter = useMemo(() => {
    switch (wizardStep) {
      case 1:
      case 4:
        return (
          <FooterWrapper>
            {hasErrors && <Text mb={2} level={5} bold type="danger">{t('delivery:pinDeliveryError')}</Text>}
            <FooterButton onClick={handleSaveMap} buttonText={t('common:save')} isDisabled={hasErrors} />
          </FooterWrapper>
          );
      case 2:
        return (
          <FooterButton
            onClick={handleSaveDetails}
            buttonText={t('delivery:confirmAddress')}
            isDisabled={hasErrors}
          />);
      case 3:
        return (
          <FooterButton
            onClick={handleSaveInstructions}
            buttonText={t('common:save')}
            isDisabled={hasErrors}
          />);
      default:
        return null;
    }
  }, [t, wizardStep, handleSaveMap, handleSaveDetails, handleSaveInstructions, hasErrors]);

  const title = useMemo(() => {
    switch (wizardStep) {
      case 1:
      case 4:
        return t('delivery:pinYourEntrance');
      case 2:
        return t('delivery:addressDetails');
      case 3:
        return t('delivery:instructions');
      default:
        return t('delivery:editAddress');
    }
  }, [t, wizardStep]);

  if(!newAddress) return null;

  return (
    <Modal
      isOpen={!!deliveryAddress}
      onBackButtonClick={wizardStep > 0 ? handleBackButton : null}
      progressValue={wizardStep === 0 ? 0 : wizardStep === 2 ? 50 : 100}
      height="660px"
      size="full-height"
      title={title}
      onRequestClose={handleClose}
      footerSections={1}
      footerRightSide={renderFooter}
      footerIgnoreThemeHeight
      isFullScreen={isMobile}
      isSticky={isMobile}
      mode="UserProfileModal"
    >
      <Wrapper>
        {(wizardStep === 0 || wizardStep === 1 || wizardStep === 4) && (
          <LocationWrapper
            componentTheme={listCardTheme}
            height={wizardStep !== 0 ? '600px' : '234px'}
            onClick={handleMapClick}
          >
            <LocationPin
              position={newAddress.address.location}
              onChange={handleChangePosition}
              isMapLocked={wizardStep === 0}
              setErrors={setErrors}
              handleAvailableForDelivery={handleAvailableForDelivery}
            />
            {wizardStep === 0 && <FixedButton mode="light" iconName="Pencil" size="small" onClick={handleMapClick}>{t('delivery:editPin')}</FixedButton>}
          </LocationWrapper>
        )}

        {wizardStep === 0 && (
          <>
            <ListCard
              name={t('delivery:addressDetails')}
              content={
                <>
                  <Text level={4} mt={1}>{get(newAddress, 'address.label', newAddress.label)}</Text>
                  <Text level={4}>{getAddressTitle(newAddress.address)}</Text>
                </>
              }
              my={6}
            >
              <IconButton icon="Pencil" mode="light" onClick={() => setWizardStep(2)} />
            </ListCard>
            <ListCard
              name={t('delivery:instructions')}
              content={
                <>
                  {newAddress.instructions.phoneNumber && <Text level={4} mt={1}>{newAddress.instructions.phoneNumber}</Text>}
                  {newAddress.instructions.deliveryInstructions && <Text level={4}>{newAddress.instructions.deliveryInstructions}</Text>}
                </>
              }
              mb={6}
            >
              <IconButton icon="Pencil" mode="light" onClick={() => setWizardStep(3)} />
            </ListCard>
          </>
        )
        }
        {wizardStep === 2 && (
          <AddressDetails
            Form={Form}
            address={newAddress.address}
            onChange={handleChangeAddressDetails}
            setErrors={setErrors}
          />
        )}
        {wizardStep === 3 && (
          <DeliveryInstructions
            Form={Form}
            address={newAddress.instructions}
            onChange={handleChangeInstructions}
            setErrors={setErrors}
          />
        )}
      </Wrapper>
    </Modal>
  );
};

const LocationWrapper = styled('div')`
  position: relative;
  height: ${({ height }) => height};
  transition: height 0.3s;
  flex: 1;
  border-radius: ${({ componentTheme }) => componentTheme.Container.borderRadius};
  box-shadow: ${({ componentTheme }) => componentTheme.Container.boxShadow};
`;

const FixedButton = styled(Button)`
  position: absolute;
  bottom: 16px;
  right: 16px;
  z-index: 1;
`;

const Wrapper = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const FooterWrapper = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;
